import { useState } from "react";
import {
  StatusChip,
  BadgeColor,
  Text,
  Divider,
  Grid,
  Button,
  useDevice,
} from "@pushpress/shared-components";
import {
  AlertDialogDescription,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  Card,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@pushpress/pushpress-ui";
import ActionsMenu from "../shared/ActionsMenu";
import ActionItem from "../shared/ActionItem";

type ReviewAndActivateCardType = {
  disableActivate: boolean;
  membersReadyForActivationCount: number;
  preventActivation: boolean;
  setIsStripeModalVisible: (value: boolean) => void;
  setIsActivateModalOpen: (value: boolean) => void;
};

export function ReviewAndActivateCard({
  disableActivate,
  membersReadyForActivationCount,
  preventActivation,
  setIsStripeModalVisible,
  setIsActivateModalOpen,
}: ReviewAndActivateCardType) {
  const { isMobile } = useDevice();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleContinue = () => {
    setIsDialogOpen(false);
    setIsActivateModalOpen(true);
  };

  return (
    <>
      <Card className="tw-p-6 tw-mt-8 tw-mb-8">
        <Grid container spacing={3}>
          <Grid item>
            <StatusChip color={BadgeColor.Gray} label="Step 3" />
          </Grid>
          <Grid item>
            <Text variant="heading-4" component="h2" color="wolf-700">
              Review & activate
            </Text>
          </Grid>
          <Grid item marginY={1} xs={12}>
            <Text variant="body-lg" color="wolf-600">
              Send a welcome email to your members to collect payment info, and
              invite them to download the members app.
            </Text>
          </Grid>
          <Divider
            color="wolf-800"
            vPadding={16}
            marginLeft={24}
            marginRight={18}
          />
          <Grid
            item
            marginY={1}
            style={{ paddingBottom: "8px", marginTop: "0", marginBottom: "0" }}
          >
            <ActionsMenu>
              <ActionItem
                description="Connect Stripe to your bank"
                url="/settings/bank"
                urlDescription="Go to settings"
              />
              <ActionItem
                description="Confirm branded app is setup (optional)"
                url="/app-store"
                urlDescription="Go to App store"
              />
            </ActionsMenu>
          </Grid>
          <Divider
            color="wolf-800"
            vPadding={16}
            marginLeft={24}
            marginRight={18}
          />
          <Grid item xs={6}>
            <Button
              data-testid="activate-button"
              buttonType="secondary"
              size="large"
              fullWidth
              disabled={disableActivate}
              text="Begin activation"
              onClick={() =>
                preventActivation
                  ? setIsStripeModalVisible(true)
                  : setIsDialogOpen(true)
              }
            />
          </Grid>
          <Grid item>
            <Divider vertical color="wolf-800" />
          </Grid>
          <Grid container item justifyContent="center" xs={5} marginY={-1}>
            <Grid item>
              <Text
                variant={isMobile ? "body-sm" : "body-md"}
                component="p"
                color="wolf-600"
              >
                Ready to activate
              </Text>
              <Text variant="heading-4" component="h4" align="center">
                {membersReadyForActivationCount}
              </Text>
            </Grid>
          </Grid>
        </Grid>
      </Card>
      <AlertDialog open={isDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Do you need to update check-ins?
            </AlertDialogTitle>
            <AlertDialogDescription>
              After activation, you will not be able to make updates to check-in
              numbers by uploading the same members again. If you need to make
              changes, click &apos;Cancel&apos; and make the changes before
              activation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleContinue}>
              No updates needed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
